import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import { useAuth } from "hooks/useAuth";
import {
  upsertPageGuideConfig,
  upsertPageGuideConfigOverride,
  setPageGuidePlatformEnabled,
  fetchPageGuidePlatformStatus,
} from "services/pageGuide/pageGuideService";
import seed from "../../../../../firebase/seed/pageGuideConfigs.json";

/**
 * Admin authoring for pageGuideConfigs + clinic overrides + kill switch.
 * Mirrors Prompt Manager patterns without overloading PromptEditorDialog.
 */
export default function PageGuideAdminPage() {
  const { isGlobalAdmin, organizationId, userData } = useAuth();
  const facilityId =
    userData?.activeInstitutionId ||
    userData?.institutionId ||
    organizationId ||
    "";

  const [platformEnabled, setPlatformEnabled] = useState(true);
  const [statusMsg, setStatusMsg] = useState("");
  const [error, setError] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const seedConfigs = Object.values(seed.configs || {});

  const {
    control,
    handleSubmit,
    reset,
  } = useForm({
    defaultValues: {
      pageId: "",
      pageContext: "",
      introductionText: "",
      steps: [],
    },
  });

  const { fields } = useFieldArray({ control, name: "steps" });

  const overrideForm = useForm({
    defaultValues: {
      pageId: "",
      enabled: true,
      nudgeText: "",
      stepId: "",
      introductionText: "",
    },
  });

  useEffect(() => {
    fetchPageGuidePlatformStatus()
      .then((s) => setPlatformEnabled(s.platformEnabled !== false))
      .catch(() => {});
  }, []);

  const openEditor = useCallback(
    (config) => {
      reset({
        pageId: config.pageId,
        pageContext: config.pageContext || "",
        introductionText: config.introductionText || "",
        steps: (config.steps || []).map((s) => ({ ...s })),
      });
      setEditorOpen(true);
    },
    [reset],
  );

  const onSaveConfig = handleSubmit(async (values) => {
    setSaving(true);
    setError("");
    setStatusMsg("");
    try {
      await upsertPageGuideConfig({
        pageId: values.pageId,
        pageContext: values.pageContext,
        introductionText: values.introductionText,
        steps: values.steps,
      });
      setStatusMsg(`Saved pageGuideConfigs/${values.pageId}`);
      setEditorOpen(false);
    } catch (err) {
      setError(err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  });

  const onSaveOverride = overrideForm.handleSubmit(async (values) => {
    if (!facilityId) {
      setError("No facility/institution on your account.");
      return;
    }
    setSaving(true);
    setError("");
    setStatusMsg("");
    try {
      await upsertPageGuideConfigOverride({
        facilityId,
        pageId: values.pageId,
        enabled: values.enabled,
        introductionText: values.introductionText,
        steps: values.stepId
          ? [{ id: values.stepId, nudgeText: values.nudgeText }]
          : [],
      });
      setStatusMsg(`Saved override for ${facilityId}__${values.pageId}`);
      setOverrideOpen(false);
    } catch (err) {
      setError(err?.message || "Override save failed");
    } finally {
      setSaving(false);
    }
  });

  const toggleKillSwitch = async () => {
    if (!isGlobalAdmin) return;
    setSaving(true);
    setError("");
    try {
      const next = !platformEnabled;
      await setPageGuidePlatformEnabled(next);
      setPlatformEnabled(next);
      setStatusMsg(`Platform PageGuide ${next ? "enabled" : "disabled"}`);
    } catch (err) {
      setError(err?.message || "Kill switch update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <DashboardPageHeader
        title="PageGuide configs"
        subtitle="Spoken introductions, idle nudges, clinic overrides, and platform kill switch"
      />

      {statusMsg ? (
        <Alert severity="success" onClose={() => setStatusMsg("")} sx={{ mb: 2 }}>
          {statusMsg}
        </Alert>
      ) : null}
      {error ? (
        <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {isGlobalAdmin ? (
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <Typography variant="body2">
            Platform kill switch: {platformEnabled ? "ON" : "OFF"}
          </Typography>
          <LoadingButton
            loading={saving}
            onClick={toggleKillSwitch}
            data-testid="pageguide-admin-killswitch"
          >
            {platformEnabled ? "Disable globally" : "Enable globally"}
          </LoadingButton>
        </Stack>
      ) : null}

      <Typography variant="h6" sx={{ mb: 1 }}>
        Seeded / known pages
      </Typography>
      <Stack spacing={1} sx={{ mb: 3 }}>
        {seedConfigs.map((cfg) => (
          <Stack
            key={cfg.pageId}
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ sm: "center" }}
          >
            <Typography variant="body2" sx={{ flex: 1 }}>
              {cfg.pageId}
            </Typography>
            {isGlobalAdmin ? (
              <Button size="small" onClick={() => openEditor(cfg)}>
                Edit global
              </Button>
            ) : null}
            <Button
              size="small"
              onClick={() => {
                overrideForm.reset({
                  pageId: cfg.pageId,
                  enabled: true,
                  stepId: cfg.steps?.[0]?.id || "",
                  nudgeText: cfg.steps?.[0]?.nudgeText || "",
                  introductionText: cfg.introductionText || "",
                });
                setOverrideOpen(true);
              }}
            >
              Clinic override
            </Button>
          </Stack>
        ))}
      </Stack>

      <Dialog open={editorOpen} onClose={() => setEditorOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Edit pageGuideConfigs</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Controller
              name="pageId"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="pageId" fullWidth disabled />
              )}
            />
            <Controller
              name="pageContext"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="pageContext" fullWidth multiline minRows={2} />
              )}
            />
            <Controller
              name="introductionText"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="introductionText (spoken on header tap)"
                  fullWidth
                  multiline
                  minRows={3}
                  helperText="40–80 words. Empty steps are allowed for intro-only pages."
                />
              )}
            />
            {fields.map((field, index) => (
              <Box key={field.id} sx={{ border: 1, borderColor: "divider", p: 1.5, borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Step {index + 1}
                </Typography>
                <Stack spacing={1}>
                  <Controller
                    name={`steps.${index}.id`}
                    control={control}
                    render={({ field: f }) => (
                      <TextField {...f} label="id" fullWidth size="small" />
                    )}
                  />
                  <Controller
                    name={`steps.${index}.selector`}
                    control={control}
                    render={({ field: f }) => (
                      <TextField {...f} label="selector (data-testid)" fullWidth size="small" />
                    )}
                  />
                  <Controller
                    name={`steps.${index}.nudgeText`}
                    control={control}
                    render={({ field: f }) => (
                      <TextField {...f} label="nudgeText" fullWidth multiline minRows={2} />
                    )}
                  />
                </Stack>
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditorOpen(false)}>Cancel</Button>
          <LoadingButton loading={saving} onClick={onSaveConfig}>
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog open={overrideOpen} onClose={() => setOverrideOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Clinic override</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Facility: {facilityId || "(none)"} — requires pageguide_content_editor or global admin
            </Typography>
            <Controller
              name="pageId"
              control={overrideForm.control}
              render={({ field }) => (
                <TextField {...field} label="pageId" fullWidth disabled />
              )}
            />
            <Controller
              name="enabled"
              control={overrideForm.control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label="Enabled for this clinic on this page"
                />
              )}
            />
            <Controller
              name="introductionText"
              control={overrideForm.control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="override introductionText (optional)"
                  fullWidth
                  multiline
                  minRows={2}
                />
              )}
            />
            <Controller
              name="stepId"
              control={overrideForm.control}
              render={({ field }) => (
                <TextField {...field} label="step id to reword" fullWidth />
              )}
            />
            <Controller
              name="nudgeText"
              control={overrideForm.control}
              render={({ field }) => (
                <TextField {...field} label="override nudgeText" fullWidth multiline minRows={2} />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverrideOpen(false)}>Cancel</Button>
          <LoadingButton loading={saving} onClick={onSaveOverride}>
            Save override
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
